import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Facebook, Twitter, Linkedin, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import BlogSidebar from "@/components/BlogSidebar";

export default function BlogPostDecompressionNeckPain() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Chronic Neck Pain and Arm Numbness? Exploring Cervical Decompression | Longview, TX"
        description="Experiencing chronic neck pain or numbness traveling down your arm? Learn how cervical spinal decompression offers non-surgical relief in East Texas."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/87897a3a-ab5c-44e3-9c3a-7899d6a614b2.jpg"
        canonical="https://woodsspine.com/blog/decompression-neck-pain"
      />
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-12">
        <Button variant="ghost" className="mb-6 hover:bg-transparent pl-0 text-muted-foreground hover:text-foreground" asChild>
          <Link to="/blog">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </Button>
        <div className="lg:grid lg:grid-cols-[1fr_350px] gap-12">
          <div className="space-y-8">
            <header className="space-y-4">
              <Badge className="bg-accent text-white hover:bg-accent/90 border-0 shadow-sm">
                Spinal Decompression
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight text-primary">
                Chronic Neck Pain and Arm Numbness? Exploring Cervical Decompression
              </h1>
              <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-4">
                <Link to="/about#dr-lance-potter" className="flex items-center hover:text-accent transition-colors">
                  <User className="w-4 h-4 mr-2" />
                  Dr. Lance Potter
                </Link>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  May 12, 2026
                </span>
                <span>• 5 min read</span>
              </div>
            </header>

            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/87897a3a-ab5c-44e3-9c3a-7899d6a614b2.jpg&w=1280&output=webp&q=80" 
              alt="Patient stretching neck experiencing chronic neck pain and arm numbness in Longview TX" 
              className="w-full rounded-2xl aspect-video object-cover"
              fetchPriority="high"
              decoding="async"
            />

            <article className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-xl font-medium text-foreground">
                While much of the focus on spinal health is centered on the lower back, chronic neck pain can be equally debilitating. If you are experiencing persistent neck stiffness, headaches, or numbness and tingling traveling down your arm into your fingers, cervical spinal decompression may be the non-surgical solution you need.
              </p>
              <p>
                The cervical spine (your neck) is incredibly complex. It supports the full weight of your head while allowing for a massive range of motion. Because of modern lifestyles—staring down at smartphones, hunching over computers, and poor sleeping postures—the discs in our neck are under more stress than ever before. When these cervical discs bulge or herniate, they can compress the delicate nerves exiting the neck, leading to severe pain and radiating arm symptoms known as cervical radiculopathy.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Why Physical Therapy Isn't Always Enough for the Neck</h2>
              <p>
                When dealing with neck pain, many patients are prescribed physical therapy to strengthen the surrounding muscles. While posture correction and strengthening are vital for long-term health, they cannot always un-pinch a trapped nerve. If a cervical disc is herniated and pressing directly against a nerve root, exercising the neck can sometimes aggravate the condition and increase the radiating arm pain.
              </p>
              <p>
                Similarly, pain medications and muscle relaxers only mask the symptoms. To truly resolve cervical radiculopathy and chronic neck pain, you must alleviate the physical pressure on the discs and nerves.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">The Mechanics of Cervical Decompression</h2>
              <p>
                <Link to="/longview-spinal-decompression" className="text-accent hover:underline">Computerized Spinal Decompression</Link> is not limited to the lower back. Specialized cervical decompression systems are designed to gently and safely stretch the neck. 
              </p>
              <p>
                During a cervical session, you lie comfortably on your back while a specialized cradle supports your head and neck. The computer applies a highly precise, gentle pulling force. This creates a vacuum effect inside the cervical discs, pulling bulging disc material away from the pinched nerves and drawing healing fluids, oxygen, and nutrients into the disc space. As the pressure is relieved, the numbness, tingling, and weakness in the arms and hands begin to fade.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Clinical Evidence and Credibility</h2>
              <p>
                Cervical spinal decompression is an FDA-cleared therapy backed by significant clinical research. Studies published in leading orthopedic and neurological journals confirm that non-surgical decompression of the cervical spine effectively reduces pain, improves range of motion, and resolves radiating arm symptoms associated with cervical disc herniations and degenerative disc disease. It provides a safe, highly effective alternative to risky cervical fusion surgeries.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Advanced Neck Care in Longview, TX</h2>
              <p>
                At Woods Spine & Wellness in Longview, TX, Dr. Lance Potter and Dr. Tucker Woods are dedicated to treating the entire spine. We know that chronic neck pain and arm numbness can make working, driving, and sleeping nearly impossible. By offering advanced cervical decompression therapy to our East Texas community, we help our patients avoid neck surgery and reclaim their lives. Discover our full approach to spinal health on our <Link to="/what-we-treat" className="text-accent hover:underline">What We Treat</Link> page.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-6 mt-6">
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Is pulling on the neck dangerous?</h3>
                  <p className="text-muted-foreground">Not when performed with a computerized decompression system. Unlike old-fashioned traction, the advanced computer sensors constantly monitor your muscle tension to ensure the pull is incredibly gentle, safe, and precise, preventing any muscle guarding or spasms.</p>
                </div>
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Can this help with frequent tension headaches?</h3>
                  <p className="text-muted-foreground">Yes. Many tension headaches and migraines originate from compressed nerves and severe muscle tension in the cervical spine. Decompressing the neck often provides profound relief for chronic headache sufferers.</p>
                </div>
                <div className="pb-2">
                  <h3 className="text-lg font-bold text-primary mb-2">How long does a cervical decompression session last?</h3>
                  <p className="text-muted-foreground">A typical cervical decompression session takes about 20 to 30 minutes. It is a relaxing, painless procedure, and patients can immediately return to their normal daily activities afterward.</p>
                </div>
              </div>

            </article>

            {/* Social Sharing */}
            <div className="flex items-center gap-4 pt-8 border-t border-border">
              <span className="font-medium text-muted-foreground">Share this article:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* CTA Block */}
            <div className="bg-secondary/30 p-8 rounded-xl border border-border mt-8">
              <h3 className="text-2xl font-bold text-primary mb-3">Relieve your neck pain today</h3>
              <p className="text-muted-foreground mb-6">
                If neck pain and arm numbness are affecting your daily life, schedule a consultation to see if cervical spinal decompression is right for you.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">Get Started</Link>
                </Button>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="text-sm text-muted-foreground border-t border-border pt-6 mt-8">
              Disclaimer: The information provided in this article is for educational purposes only and does not constitute medical advice. Please consult with a healthcare provider before starting any new treatment.
            </div>
          </div>

          <BlogSidebar />
        </div>
      </main>
      <Footer />
    </div>
  );
}
